import { z } from "zod";
import type { Performance, StorySceneHeader } from "./performances";

const id = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
/** Another view of an existing character, independently staged for this scene only. */
export const zStoryRepresentation = z
  .object({
    id,
    sourceRoleId: id,
    name: z.string().min(1).max(200).optional(),
    /** An existing source-role appearance key; omitted uses the usual appearance. */
    persona: id.optional(),
  })
  .strict();
export type StoryRepresentation = z.infer<typeof zStoryRepresentation>;
export const zStoryRepresentations = z
  .array(zStoryRepresentation)
  .max(12)
  .superRefine((entries, ctx) => {
    const seen = new Set<string>();
    entries.forEach((entry, index) => {
      if (seen.has(entry.id))
        ctx.addIssue({ code: "custom", path: [index, "id"], message: "Each scene representation needs its own id." });
      seen.add(entry.id);
    });
  });

/** Resolve only from this scene and the authorized, real cast; never chain aliases. */
export function scenePerformanceCast<T extends Pick<Performance, "cast">>(
  performance: T,
  scene: Pick<StorySceneHeader, "representations" | "temporalFrames">
): T {
  const representations = zStoryRepresentations.parse(scene.representations || []);
  const byId = new Map(performance.cast.map((role) => [role.id, role]));
  const aliases = representations.map((representation) => {
    if (byId.has(representation.id))
      throw new Error("A scene representation cannot replace a cast role: " + representation.id);
    const source = byId.get(representation.sourceRoleId);
    if (!source || source.unassigned) throw new Error("Unknown representation source: " + representation.sourceRoleId);
    const variants = source.variants as Record<string, string> | undefined;
    if (
      representation.persona &&
      !source.variantPersonaIds?.[representation.persona] &&
      !variants?.[representation.persona]
    )
      throw new Error("Unknown representation appearance: " + representation.persona);
    const name = representation.name || source.name;
    const appearancePoses =
      representation.persona && source.appearancePoses && typeof source.appearancePoses === "object"
        ? (source.appearancePoses as Record<string, { poseDescriptions?: unknown; poseConstraints?: unknown }>)[
            representation.persona
          ]
        : undefined;
    return {
      ...source,
      id: representation.id,
      name,
      displayName: name,
      sourceRoleId: source.id,
      ...(appearancePoses && typeof appearancePoses === "object" ? appearancePoses : {}),
      ...(representation.persona
        ? {
            jsonName: variants?.[representation.persona] || source.jsonName || source.name,
            personaId: source.variantPersonaIds?.[representation.persona],
          }
        : {}),
    };
  });
  const frameRoles = new Set([...byId.keys(), ...aliases.map((role) => role.id)]);
  for (const frame of scene.temporalFrames || [])
    for (const role of frame.roleIds)
      if (!frameRoles.has(role)) throw new Error("Unknown temporal frame role: " + role);
  return { ...performance, cast: [...performance.cast, ...aliases] };
}

export function storyRepresentationSource(
  scene: Pick<StorySceneHeader, "representations">,
  characterId: string
): string {
  return scene.representations?.find((entry) => entry.id === characterId)?.sourceRoleId || characterId;
}
